import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "./DashboardLayout/DasboardLayout";
import { API_URL } from "./config";

const BASE = `${API_URL}/shared-ride-admin`;

const SECTIONS = [
  {
    title: "Seats",
    fields: [
      { key: "max_seats_per_trip", label: "Maximum seats per shared trip", step: 1, help: "The most seats a driver can offer on one shared trip (1 to 6). New trips asking for more are refused with a clear message. Existing trips are not changed. Note: the WhatsApp bot currently asks for 1 to 3 seats and the web app starts at 3, regardless of this value." },
    ],
  },
  {
    title: "Passenger requests",
    fields: [
      { key: "request_expiry_minutes", label: "Request expiry (minutes)", step: 1, help: "A shared-ride request that no driver accepts in this time expires and the passenger is offered a private ride instead (1 to 60)." },
      { key: "max_active_requests", label: "Active requests per passenger", step: 1, help: "How many open shared-ride requests one passenger may have at the same time." },
    ],
  },
  {
    title: "Matching",
    fields: [
      { key: "detour_limit_km", label: "Detour limit (km)", step: 0.1, help: "The most extra distance a driver can be asked to travel to collect a passenger on his route. The allowed range comes from the matching settings." },
    ],
  },
  {
    title: "Driver notifications and visibility",
    fields: [
      { key: "notify_radius_km", label: "Notify drivers within (km)", step: 0.5, help: "When a passenger posts a shared-ride request, drivers inside this radius are notified." },
      { key: "notify_closest_drivers", label: "Notify the closest (drivers)", step: 1, help: "At most this many of the nearest drivers are notified." },
      { key: "nearby_radius_km", label: "Driver request list radius (km)", step: 0.5, help: "Drivers see open passenger requests within this distance on their requests list. This is the radius previously labelled as the detour limit on the Realtime Settings page." },
    ],
  },
];

function Field({ field, value, onChange }) {
  return (
    <div className="mb-4">
      <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200">{field.label}</label>
      <input type="number" step={field.step} min="0" value={value ?? ""} onChange={(e) => onChange(field.key, e.target.value)} className="w-full rounded-lg border p-2" />
      <p className="mt-1 text-xs text-gray-500">{field.help}</p>
    </div>
  );
}

export default function SharedRideSettings() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await (await fetch(`${BASE}/overview`)).json();
      if (!res.success) throw new Error(res.message || "Failed to load");
      setData(res.data);
      setForm(res.data.settings);
    } catch (e) {
      setMessage(`Failed to load: ${e.message}`);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const change = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const save = async () => {
    setSaving(true);
    setMessage("");
    try {
      const body = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v === "" ? v : Number(v)]));
      const res = await fetch(`${BASE}/settings`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.message || "Failed to save");
      setData(json.data);
      setForm(json.data.settings);
      setMessage(json.data.changed.length ? `Saved: ${json.data.changed.join(", ")}. Changes apply immediately.` : "Nothing changed.");
    } catch (e) {
      setMessage(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!form || !data) {
    return (
      <DashboardLayout>
        <div className="p-6">{message || "Loading..."}</div>
      </DashboardLayout>
    );
  }

  const today = data.today;

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-4xl p-6">
        <h2 className="mb-1 text-2xl font-bold text-gray-800 dark:text-white">Shared Rides</h2>
        <p className="mb-6 text-sm text-gray-500">
          Settings for shared rides, where a driver offers seats on his route and passengers join. Every change is recorded in the settings audit log. Some of these also appear on the{" "}
          <Link to="/commission-control" className="text-blue-600 underline">Realtime Settings</Link> page; both edit the same values.
          Morning Dash has its own page.
        </p>

        {SECTIONS.map((section) => (
          <div key={section.title} className="mb-6 rounded-lg bg-white p-5 shadow dark:bg-gray-800">
            <h3 className="mb-4 text-lg font-semibold text-gray-800 dark:text-white">{section.title}</h3>
            {section.fields.map((f) => (
              <Field key={f.key} field={f} value={form[f.key]} onChange={change} />
            ))}
          </div>
        ))}

        {message && <p className="mb-4 text-sm text-gray-700 dark:text-gray-200">{message}</p>}
        <button onClick={save} disabled={saving} className="mb-8 rounded-lg bg-blue-600 px-5 py-2 text-white disabled:opacity-50">
          {saving ? "Saving..." : "Save settings"}
        </button>

        <div className="mb-6 rounded-lg bg-white p-5 shadow dark:bg-gray-800">
          <h3 className="mb-2 text-lg font-semibold text-gray-800 dark:text-white">Today ({today.date})</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="mb-1 font-medium">Trips posted</p>
              {Object.keys(today.trips).length ? Object.entries(today.trips).map(([s, n]) => <p key={s}>{s}: {n}</p>) : <p className="text-gray-500">None today</p>}
            </div>
            <div>
              <p className="mb-1 font-medium">Passenger requests</p>
              {Object.keys(today.requests).length ? Object.entries(today.requests).map(([s, n]) => <p key={s}>{s}: {n}</p>) : <p className="text-gray-500">None today</p>}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
